import { Checkbox, FormControl, FormControlLabel, Grid, InputLabel, MenuItem, Select, Typography, IconButton, Tooltip } from '@mui/material';
import { blue, brown, green, orange, red, yellow } from '@mui/material/colors';
import { ContactSupportOutlined, Clear as ClearIcon } from '@mui/icons-material';
import * as React from 'react';
import { useEffect, useMemo } from 'react';
import { LocalizeContextProps, Translate, withLocalize } from 'react-localize-redux';
import { useSelector } from 'react-redux';
import { EnhancedTable } from '../../../components/general/EnhancedTable';
import { TypographyStyled } from '../../../components/general/mini_components';
import { ColourChip } from '../../../components/general/mini_components-ts';
import Loader from '../../../components/Loader';
import { HeadCell, IPatient, IResearcher, ISurvey } from '../../../constants/types';
import { useDepartments, useCurrentInvestigation } from '../../../hooks';
import { usePDFConfig } from '../../../hooks/usePDFConfig';
import { dateAndTimeFromPostgresString, decryptSinglePatientData, formatBirthdateForDisplay, fullDateFromPostgresString, getDepartmentFromUnit, getHealthIDLabel, getPatientID, patientFullName, researcherFullName, stringDatePostgresToDate, formatDateByLocale } from '../../../utils/index.jsx';
import { formatRequestedSampling, isRequestedSampling, RequestedSampling } from './requestedSampling';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import dayjs, { Dayjs } from 'dayjs';
import axios from '../../../utils/axios';
import { useRequests } from '../../../hooks/useRequests';
import { useSurgeryAppointments } from '../../../hooks/useSurgeryAppointments';

import { IRequest, RequestStatus, RequestType, ServiceType } from './types';
import { buildRequestTableColumns } from './requestTableColumns';
import { requesterServiceName, REQUESTER_NOT_RECORDED } from './requesterServiceName';
import SearchBox from '../../../components/general/SearchBox';
import { calculateTotalDays, getRequestStatusName, labStatusValues, shoeStatusValues, statusToColor, surgeryStatusValues } from './utils';
import { fetchPatient } from '../../../db';
import { getSubmissionPatientService } from '../../../services';
import Modal from '../../../components/general/modal';
import { DocumentPDF } from '../Document';
import { RequestPDFDocument } from '../../../components/pdf/documents/RequestPDFDocument';
import { PDFDownloadButton } from '../../../components/pdf/components/PDFDownloadButton';
import CancelRequestAction from './CancelRequestAction';
import { SnackbarTypeSeverity } from '../../../constants/types';
import { createT } from '../../../components/pdf/utils/simpleTranslate';
import { formatSurgeryCim11 } from '../../../utils/surgeryRequestCim11';
import { buildSurgicalListCsv, SurgicalListLabels, SurgicalListRow } from './surgicalListExport';
import { downloadCsv } from '../../../utils/csvDownload';
import ExcelLogo from '../../../img/icons/excel-logo.png';
import PrintIcon from '@mui/icons-material/Print';

// SHER-462: render the CIM-11 diagnosis truncated with an ellipsis and the full
// text on hover, so the waiting-list table stays narrow but the diagnosis is
// still reachable. "-" (no diagnosis) is shown plain, without a tooltip.
const renderCim11Cell = (value: string): React.ReactNode => {
    if (!value || value === '-') {
        return '-';
    }
    return (
        <Tooltip title={value}>
            <span
                style={{
                    display: 'inline-block',
                    maxWidth: 220,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    verticalAlign: 'middle',
                }}
            >
                {value}
            </span>
        </Tooltip>
    );
};

// Exported for SHER-908: the short-circuit below is one of the places where forgetting the
// new status silently HIDES it, so it is pinned by a test rather than left to review.
export const requestGroupStatus = (request:IRequest) => {
    // SHER-908: delivered outranks completed. The COMPLETED short-circuit below is reached
    // by a delivered cordonnerie order too — its items carry MATERIAL_DELIVERED, but the
    // group row would still have to answer something, and answering "Acte effectué" would
    // hide the handover the shop just recorded.
    if(request.requestsServiceInvestigation.some(item => item.status === RequestStatus.MATERIAL_DELIVERED)){
        return <RequestStatusToChip status={RequestStatus.MATERIAL_DELIVERED} />
    }
    // Si una request es completada, el estado es completada. Si no, devuelvo los estados de las requests
    if(request.requestsServiceInvestigation.some(request => request.status === RequestStatus.COMPLETED)){
        return <RequestStatusToChip status={RequestStatus.COMPLETED} />
    }
    else{
        return request.requestsServiceInvestigation.map((req) => {
            return <RequestStatusToChip status={req.status} />
        }) 
    }
    
}

// SHER-935: statusToColor now lives in ./utils, a dependency-free module, so it can be unit
// tested — this file's import chain reaches the jose ESM build jest cannot parse, which is
// why its colour cases had never been covered. Re-exported so the two external importers
// (PharmacyRequestTable, RequestInfo) and their existing module mocks keep working unchanged.
export { statusToColor };


export const RequestStatusToChip:React.FC<{status:RequestStatus; labelPrefix?: string}> = ({status, labelPrefix = 'pages.hospital.services.request_status'}) =>  {
        let colour = statusToColor(status);
        let translation = RequestStatus[status]
       
        return <ColourChip rgbcolor={colour} label={<Translate id={`${labelPrefix}.${translation}`}  />}/>
}

export const serviceToColor = (type:ServiceType) => {
    let colour = null
    switch(type){
        case ServiceType.LABORATORY:
            colour = orange[900];
            break;
        case ServiceType.IMAGING:
            colour = yellow[900];
            break;
        case ServiceType.PHARMACY:
            colour = green[900];
            break;
        case ServiceType.SHOE:
            colour = brown[900];
            break;
    }
    return colour;
}

const ServiceTypeToChip:React.FC<{type:ServiceType}> = ({type}) =>  {
    let colour = serviceToColor(type);
    let translation = ServiceType[type];
    return <ColourChip rgbcolor={colour} label={<Translate id={`pages.hospital.services.service_type.${translation}`}/>} />
}


interface RequestTableProps {
    requestServiceType:ServiceType,
    activeLanguageCode:string,
    typeRequest:RequestType,
    uuidPatient?:string,
    noSearchBox?:boolean,
    uuidInvestigation:string,
    showActions?:boolean,
    surveys?:ISurvey[],
    searchFor:string,
    fillPending?:boolean,
    patient?:IPatient, // Add optional patient prop
    encryptionData?:{
        encryptedKeyUsed:number,
        keyResearcherInvestigation:string,
        permissions:any[],
        personalFields:any[],
    },
    callBackRequestSelected:(request:IRequest)=>void,
    createAppointmentFromRequestCallback:(requestId:number) => void,
    goToAppointmentFromRequestCallback:(uuidAppointment:string) => void,
    goToBillingPatientCallback:(uuidPatient:string) => void,
    permissions?: string[],
    setShowSnackbar?: (snackbar: { show: boolean; message?: string; severity?: SnackbarTypeSeverity }) => void,
}

const ACTIONABLE_REQUESTS = [RequestStatus.PENDING_APPROVAL, RequestStatus.ACCEPTED, RequestStatus.COMPLETED, RequestStatus.IN_PROGRESS, RequestStatus.PENDING_ANESTHESIOLOGIST, RequestStatus.PENDING_PURCHASE_SURGERY_PROTHESIS];

export const RequestTableService:React.FC<RequestTableProps> = ({requestServiceType, uuidPatient, uuidInvestigation, showActions, activeLanguageCode,
                                                                    surveys, fillPending, encryptionData, patient, callBackRequestSelected, 
                                                                    createAppointmentFromRequestCallback, goToAppointmentFromRequestCallback, goToBillingPatientCallback}) => {
    
    return <RequestTable requestServiceType={requestServiceType} uuidPatient={uuidPatient} searchFor="pages.hospital.services.search_box.label"
                uuidInvestigation={uuidInvestigation} showActions={showActions} surveys={surveys} typeRequest={RequestType.SERVICES}
                fillPending={fillPending} encryptionData={encryptionData} activeLanguageCode={activeLanguageCode}
                patient={patient}
                createAppointmentFromRequestCallback={createAppointmentFromRequestCallback}
                goToAppointmentFromRequestCallback={goToAppointmentFromRequestCallback}
                goToBillingPatientCallback={goToBillingPatientCallback}
                callBackRequestSelected={callBackRequestSelected} />
}

const RequestTable: React.FC<RequestTableProps> = ({
    typeRequest, 
    requestServiceType,
    searchFor, 
    noSearchBox, 
    surveys, 
    activeLanguageCode,
    uuidPatient, 
    uuidInvestigation, 
    encryptionData, 
    showActions,
    fillPending,
    patient, // Add patient prop
    callBackRequestSelected, 
    createAppointmentFromRequestCallback,
    goToAppointmentFromRequestCallback,
    goToBillingPatientCallback,
    permissions,
    setShowSnackbar,
}) => {
    const { 
        requests, 
        loading: requestsLoading, 
        error: requestsError,
        refetch: refetchRequests,
    } = useRequests({
        requestServiceType,
        uuidPatient,
        uuidInvestigation,
        typeRequest
    });

    const { 
        appointmentsByRequest, 
        loading: appointmentsLoading, 
        error: appointmentsError 
    } = useSurgeryAppointments({
        uuidInvestigation,
        enabled: requestServiceType === ServiceType.SURGERY
    });

    const enrichedRequests = useMemo(() => {
        if (!requests) return [];

        if(appointmentsByRequest.length === 0){
            return requests;
        }
        
        return requests.map(request => {
            const appointmentWithRequest = appointmentsByRequest[request.id];
            return {
                ...request,
                appointment: appointmentWithRequest ? appointmentWithRequest : null
            }
        });
    }, [requests, appointmentsByRequest]);


    if (requestsLoading || appointmentsLoading || !requests) {
        return <Loader />;
    }

    if (requestsError || appointmentsError) {
        console.error('Errors:', { requestsError, appointmentsError });
        // You might want to show an error message to the user
    }

    if (requests.length > 0) {
        return (
            <RequestTableComponent 
                searchFor={searchFor} 
                noSearchBox={noSearchBox} 
                requestServiceType={requestServiceType}
                surveys={surveys} 
                fillPending={fillPending}  
                encryptionData={encryptionData} 
                uuidPatient={uuidPatient}
                uuidInvestigation={uuidInvestigation}
                showActions={showActions}
                activeLanguageCode={activeLanguageCode}
                patient={patient}
                loading={requestsLoading || appointmentsLoading} 
                requests={enrichedRequests} 
                callBackRequestSelected={callBackRequestSelected}
                createAppointmentFromRequestCallback={createAppointmentFromRequestCallback}
                goToAppointmentFromRequestCallback={goToAppointmentFromRequestCallback}
                goToBillingPatientCallback={goToBillingPatientCallback}
                permissions={permissions}
                setShowSnackbar={setShowSnackbar}
                refetchRequests={refetchRequests}
            />
        );
    }

    return (
        <TypographyStyled variant="body2">
            <Translate id="pages.hospital.services.no_requests" />
        </TypographyStyled>
    );
};

export default RequestTable;

interface RequestTableComponentProps extends RequestTableProps {
    requests:IRequest[],
    loading:boolean,
    createAppointmentFromRequestCallback:(requestId:number) => void,
    goToAppointmentFromRequestCallback:(uuidAppointment:string) => void,
    goToBillingPatientCallback:(uuidPatient:string) => void,
    refetchRequests?: () => Promise<void>,
}

interface Row {
    id: number;
    nhc: string | number;
    service: string | JSX.Element;
    serviceName: string; // Add missing property
    department: string;
    // SHER-910 — the origin service snapshotted when the request was created, already rendered:
    // "Department — Unit", "Department", or the translated "not recorded" label when the API never
    // determined one. NOT the doctor's current department, which is what SHER-134 used to put here.
    requesterDepartment: string;
    patient: string;
    dob: string;
    researcher: string;
    requester: string;
    // SHER-722: the result submission's author; empty until a result exists.
    reporter: string;
    requestedItems: string;
    cim11?: React.ReactNode;
    cim11Text?: string; // SHER-462 — full, untruncated CIM-11 for CSV export
    mainSurgeon?: string;
    dateCompleted?: string;
    totalDays?: number | string;
    dateAdded: string;
    requestedSampling?: string;
    submissionPatient: any;
    status: JSX.Element;
    statusValue: RequestStatus;
    type: string | JSX.Element;
    date: string;
    scheduled: boolean; // Add missing property
    uuidResearcher: string; // Add missing property
    dateCompletedRaw: Date | null; // Raw completion date for filtering
}
export const RequestTableComponent: React.FC<RequestTableComponentProps> = ({ uuidPatient, uuidInvestigation, searchFor, requests, requestServiceType, noSearchBox, surveys,
                                                                                encryptionData, loading, patient, callBackRequestSelected, createAppointmentFromRequestCallback, 
                                                                                goToAppointmentFromRequestCallback, goToBillingPatientCallback, activeLanguageCode,
                                                                                permissions, setShowSnackbar, refetchRequests }) => {
    const requestsById = useMemo(() => {
        const map = new Map<number, IRequest>();
        requests.forEach((request) => map.set(request.id, request));
        return map;
    }, [requests]);
    
    // Normalize locale: ensure it's 'en', 'es', or 'fr'
    const normalizedLocale = activeLanguageCode?.toLowerCase().startsWith('en') ? 'en' : 
                             activeLanguageCode?.toLowerCase().startsWith('es') ? 'es' : 'fr';
    const t = createT(normalizedLocale);
    
    const {departments} = useDepartments();
    const { isBillingEnabled, isPdfExportEnabled } = useCurrentInvestigation();
    const pdfConfig = usePDFConfig(); // Add PDF configuration hook
    
    // Get investigation UUID for signature fetching
    const currentInvestigationUuid = useSelector((state: any) => 
        state.investigations.currentInvestigation?.uuid
    );    
    const [rows, setRows] = React.useState<Row[]>([]);
    const [filteredRows, setFilteredRows] = React.useState<Row[]>([]);
    const [searchText, setSearchText] = React.useState("");
    const [statusFilter, setStatusFilter] = React.useState<RequestStatus[]>([]);
    const [scheduledFilter, setScheduledFilter] = React.useState<boolean>(false);
    const [stringHealthID, setStringHealthID] = React.useState(getHealthIDLabel());

    const [proceduresApplied, setProceduresApplied] = React.useState<string[]>([]);
    const [procedureFilter, setProcedureFilter] = React.useState<string>("");
    const [surgeonsFilter, setSurgeonsFilter] = React.useState<string>("");
    const [surgeonsScheduled, setSurgeonsScheduled] = React.useState<IResearcher[]>([]);
    const [startDateFilter, setStartDateFilter] = React.useState<Dayjs | null>(null);
    const [endDateFilter, setEndDateFilter] = React.useState<Dayjs | null>(null);
    const [showModalPDF, setShowModalPDF] = React.useState<boolean>(false);
    const [requestPDF, setRequestPDF] = React.useState<IRequest | null>(null);
    const [patientForPDF, setPatientForPDF] = React.useState<IPatient | null>(null);
    const [fullSubmission, setFullSubmission] = React.useState<any>(null);
    const [loadingPDFData, setLoadingPDFData] = React.useState<boolean>(false);
    const [tablePage, setTablePage] = React.useState(0);

    const filterItems = useMemo(() => {
        // SHER-908: cordonnerie gets its own list. labStatusValues is shared with Laboratory
        // and Radiology, where no request can ever reach MATERIAL_DELIVERED, so widening it
        // would put "Matériel délivré" in two menus that can never show it.
        // Note for anyone diffing tsc output: adding this third branch re-words a
        // pre-existing error below (filterItems pushes a boolean into `value`), because the
        // union tsc prints now includes the shoe list. Same single error, new wording.
        const requestStatusValues = requestServiceType === ServiceType.SURGERY
            ? surgeryStatusValues
            : requestServiceType === ServiceType.SHOE
                ? shoeStatusValues
                : labStatusValues;
        
        let filterItems = requestStatusValues.map((status) => ({label:`pages.hospital.services.request_status.${getRequestStatusName(status)}`, selected: statusFilter.includes(status), value:status, color:statusToColor(status), callBack:() => applyStatusFilter(status)}))
        if(requestServiceType === ServiceType.SURGERY){
            filterItems.push({label:`pages.hospital.services.request_status.scheduled`, selected: scheduledFilter, value:scheduledFilter, color:statusToColor(RequestStatus.READY_FOR_SURGERY), callBack:() => setScheduledFilter(!scheduledFilter)})
        }
        return filterItems;
    }, [statusFilter, scheduledFilter]);

    useEffect(() => {
        const uniqueProcedures = requests.reduce((acc: string[], request) => {
            // Optional chaining — a sparse first element (or a partial backend response missing `serviceInvestigation`)
            // was crashing the /pharmacy page with "Cannot read properties of undefined (reading 'serviceInvestigation')".
            // Observed on chom-dakar 2026-04-20 via diagnostics breadcrumbs.
            const procedureName = request.requestsServiceInvestigation?.[0]?.serviceInvestigation?.service?.name ?? "";
            if (!acc.includes(procedureName)) {
                acc.push(procedureName);
            }
            return acc;
        }, []);
        setProceduresApplied(uniqueProcedures);
    }, [requests]);

    useEffect(() => {
        const uniqueStaffs = requests.reduce((acc: IResearcher[], request) => {
            const mainSurgeon = request.appointment ? request.appointment.mainSurgeon?.researcher : null;
            if(mainSurgeon){
                const exists = acc.some(staff => staff.uuid === mainSurgeon.uuid);
                if (!exists) {
                acc.push(mainSurgeon);
            }}
            return acc;
        }, []);
        setSurgeonsScheduled(uniqueStaffs);
    }, [requests]);

    useEffect(() => {
        const loadHealthID = async () => {
            if (requests.length <= 1) {
                return;
            }
            if (requests.length > 1) {
                const patient = requests[0].requestsServiceInvestigation.length > 0 ? requests[0].requestsServiceInvestigation[0].patientInvestigation : null;
                const patientData = patient ? await fetchPatient(patient.uuid) : null;
                const label = getHealthIDLabel(patientData);
                setStringHealthID(label);
            }
        };
        loadHealthID();
    }, [activeLanguageCode, requests]);

    useEffect(() => {
        const normalizedSearch = searchText.trim();
        setFilteredRows(rows.filter((row: Row) => {
            const statusFiltered = statusFilter.length === 0 ? true : statusFilter.includes(row.statusValue);

            const searchItem = row.patient;
            const healthIdFiltered = row.nhc.toString().toLocaleLowerCase().includes(normalizedSearch.toLocaleLowerCase());
            const scheduledFiltered = scheduledFilter ? row.scheduled : true;
            const procedureFiltered = procedureFilter !== "" ? row.serviceName.toString().toLocaleLowerCase().includes(procedureFilter.toLocaleLowerCase()) : true;
            const staffFiltered = surgeonsFilter !== "" ? row.uuidResearcher === surgeonsFilter : true;
            
            // Date range filtering for completion date
            let dateFiltered = true;
            if (requestServiceType === ServiceType.SURGERY && row.dateCompletedRaw) {
                const completedDate = stringDatePostgresToDate(row.dateCompletedRaw);
                if (startDateFilter) {
                    const startDate = startDateFilter.startOf('day').toDate();
                    if (completedDate < startDate) {
                        dateFiltered = false;
                    }
                }
                if (endDateFilter && dateFiltered) {
                    const endDate = endDateFilter.endOf('day').toDate();
                    if (completedDate > endDate) {
                        dateFiltered = false;
                    }
                }
            } else if (requestServiceType === ServiceType.SURGERY && (startDateFilter || endDateFilter)) {
                // If date filters are set but row has no completion date, exclude it
                dateFiltered = false;
            }
            
            const filtered = (scheduledFilter || healthIdFiltered || searchItem.toLocaleLowerCase().includes(normalizedSearch.toLocaleLowerCase())) && statusFiltered && scheduledFiltered && procedureFiltered && staffFiltered && dateFiltered;
            return filtered;
                        
        }));
        
    }, [searchText, statusFilter, scheduledFilter, procedureFilter, surgeonsFilter, startDateFilter, endDateFilter, rows, requestServiceType]);

    useEffect(() => {
        setTablePage(0);
    }, [searchText, statusFilter, scheduledFilter, procedureFilter, surgeonsFilter, startDateFilter, endDateFilter]);
    
    useEffect(() => {
        const loadRows = async () => {
            const rowPromises = requests.sort((reqA, reqB) => 
                stringDatePostgresToDate(reqB.updatedAt).getTime() - stringDatePostgresToDate(reqA.updatedAt).getTime()
            ).map(async (request) => {
                const uuidPatient = request.requestsServiceInvestigation[0] ? request.requestsServiceInvestigation[0].patientInvestigation.uuid : "";
                const patient = await fetchPatient(uuidPatient);
                console.log("uuidPatient", uuidPatient);
                console.log("requestId", request.id);
                
                const mainSurgeon = request.appointment?.mainSurgeon ? request.appointment.mainSurgeon : null;
                const mainSurgeonName = mainSurgeon ? researcherFullName(mainSurgeon.researcher) : "";
                const uuidResearcher = mainSurgeon ? mainSurgeon?.researcher.uuid : "";
                return {
                    id: request.id,
                    nhc: patient ? getPatientID(patient) : "",
                    service: request.requestsServiceInvestigation[0]?.serviceInvestigation?.service?.name ? request.requestsServiceInvestigation.map((req) => <ColourChip rgbcolor={serviceToColor(request.type)} label={req.serviceInvestigation.description} /> ) : "",
                    serviceName: request.requestsServiceInvestigation?.[0]?.serviceInvestigation?.service?.name ?? "",
                    department:request.departmentRequest ? request.departmentRequest.name : "",
                    requesterDepartment: requesterServiceName(request) ?? t(REQUESTER_NOT_RECORDED),
                    patient: patient ? patientFullName(patient?.personalData) : "",
                    dob : patient?.personalData.birthdate ? formatBirthdateForDisplay(patient.personalData.birthdate, activeLanguageCode) : "",
                    researcher: researcherFullName(request.researcher),
                    requester: researcherFullName(request.researcher),
                    reporter: request.submissionPatient?.researcher ? researcherFullName(request.submissionPatient.researcher) : "",
                    requestedItems: "",
                    cim11: renderCim11Cell(formatSurgeryCim11(request.typeSpecificData)),
                    cim11Text: formatSurgeryCim11(request.typeSpecificData),
                    mainSurgeon: mainSurgeonName,
                    uuidResearcher : uuidResearcher,        
                    status: <RequestStatusToChip status={request.status} />,
                    dateCompleted: request.completedAt ? fullDateFromPostgresString(activeLanguageCode, request.completedAt) : "",
                    dateCompletedRaw: request.completedAt ? request.completedAt : null,
                    submissionPatient: request.submissionPatient,
                    statusValue: request.status,
                    type : request.requestsServiceInvestigation?.[0]?.serviceInvestigation?.service?.type
                        ? <ServiceTypeToChip type={request.requestsServiceInvestigation[0].serviceInvestigation.service.type} />
                        : "",
                    scheduled : request.appointment ? true : false,
                    totalDays: calculateTotalDays(request, requestServiceType),
                    // SHER-1043: request date/time includes the clock; sampling is Routine when absent.
                    dateAdded: dateAndTimeFromPostgresString(activeLanguageCode, request.createdAt),
                    dateAddedRaw: request.createdAt,
                    requestedSampling: formatRequestedSampling(
                        isRequestedSampling((request.typeSpecificData as { requestedSampling?: RequestedSampling } | null)?.requestedSampling)
                            ? (request.typeSpecificData as { requestedSampling: RequestedSampling }).requestedSampling
                            : undefined,
                        (id) => String(t(id)),
                        (iso) => dateAndTimeFromPostgresString(activeLanguageCode, iso),
                    ),
                }
            });
            
            const rows = await Promise.all(rowPromises);
            setRows(rows);
            setFilteredRows(rows);
        };

        loadRows();
    }, [requests, activeLanguageCode]);

    function applyFilterGeneral(filter:number[] , functionAdd: (value:number[]) => void, value:number){
        if(filter && filter.includes(value)){
            if(filter.length === 1){
                functionAdd([]);
            }
            else{
                functionAdd(filter.filter((s) => s !== value));
            }
        }
        else if(filter){
            functionAdd([...filter, value]);
        }
        else{
            functionAdd([value]);
        }
    }

    function applyStatusFilter(status:RequestStatus){
        applyFilterGeneral(statusFilter, setStatusFilter, status);
    }

    function iconType(id:number){
        const request = requests.find((req) => req.id === id);
        if(request && request.appointment){
            return "appointment_available";
        }
        return "appointment";
    }

    function colorOfAppointment(id:number){
        const request = requests.find((req) => req.id === id);
        if(request && request.appointment){
            return "green";
        }
        return "black";
    }

    function extraFilters(){
        if(requestServiceType === ServiceType.SURGERY){
            return (
                <Grid container spacing={2}>
                    <Grid item>
                        <FormControl variant="outlined" size="small" style={{ minWidth: '300px' }}>
                            <InputLabel htmlFor="select-procedure" id="select-procedure-label" style={{backgroundColor:'white'}}>
                                <Translate id="hospital.request.select_procedure" />
                            </InputLabel>
                            <Select
                                labelId="select-procedure-label"
                                id="select-procedure"
                                style={{ minWidth: '200px' }}
                                value={procedureFilter}
                                onChange={(e) => setProcedureFilter(e.target.value as string)}
                                displayEmpty
                                renderValue={(value) => value }
                                endAdornment={
                                    procedureFilter ? (
                                        <IconButton
                                            size="small"
                                            style={{ marginRight: 8 }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setProcedureFilter("");
                                            }}
                                        >
                                            <ClearIcon fontSize="small" />
                                        </IconButton>
                                    ) : null
                                }
                            >
                                {proceduresApplied.map((procedure) => (
                                    <MenuItem key={procedure} value={procedure}>{procedure}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item>
                        <FormControl variant="outlined" size="small" style={{ minWidth: '300px' }}>
                            <InputLabel htmlFor="select-surgeon" id="select-surgeon-label" style={{backgroundColor:'white'}}>
                                <Translate id="pages.hospital.services.request.select_surgeon" />
                            </InputLabel>
                            <Select
                                labelId="select-surgeon-label"
                                id="select-surgeon"
                                style={{ minWidth: '200px' }}
                                value={surgeonsFilter}
                                onChange={(e) => setSurgeonsFilter(e.target.value as string)}
                                displayEmpty
                                renderValue={(value) => {
                                    if (!value) return <Translate id="pages.hospital.services.request.select_surgeon" />;
                                    const staff = surgeonsScheduled.find(s => s.uuid === value);
                                    return staff ? researcherFullName(staff) : <Translate id="pages.hospital.services.request.select_surgeon" />;
                                }}
                                endAdornment={
                                    surgeonsFilter ? (
                                        <IconButton
                                            size="small"
                                            style={{ marginRight: 8 }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setSurgeonsFilter("");
                                            }}
                                        >
                                            <ClearIcon fontSize="small" />
                                        </IconButton>
                                    ) : null
                                }
                            >
                                {surgeonsScheduled.map((researcher) => (
                                    <MenuItem key={researcher.uuid} value={researcher.uuid}>{researcherFullName(researcher)}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item>
                        <div style={{ position: 'relative', minWidth: '200px' }}>
                            <DesktopDatePicker
                                label={<Translate id="hospital.request.date_completed_start" />}
                                value={startDateFilter}
                                onChange={(newValue) => setStartDateFilter(newValue)}
                                format={formatDateByLocale(activeLanguageCode)}
                                slotProps={{
                                    textField: {
                                        size: 'small',
                                        fullWidth: true,
                                    },
                                }}
                            />
                            {startDateFilter && (
                                <IconButton
                                    size="small"
                                    style={{ 
                                        position: 'absolute', 
                                        right: 8, 
                                        top: '50%', 
                                        transform: 'translateY(-50%)',
                                        zIndex: 1
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setStartDateFilter(null);
                                    }}
                                >
                                    <ClearIcon fontSize="small" />
                                </IconButton>
                            )}
                        </div>
                    </Grid>
                    <Grid item>
                        <div style={{ position: 'relative', minWidth: '200px' }}>
                            <DesktopDatePicker
                                label={<Translate id="hospital.request.date_completed_end" />}
                                value={endDateFilter}
                                onChange={(newValue) => setEndDateFilter(newValue)}
                                format={formatDateByLocale(activeLanguageCode)}
                                slotProps={{
                                    textField: {
                                        size: 'small',
                                        fullWidth: true,
                                    },
                                }}
                            />
                            {endDateFilter && (
                                <IconButton
                                    size="small"
                                    style={{ 
                                        position: 'absolute', 
                                        right: 8, 
                                        top: '50%', 
                                        transform: 'translateY(-50%)',
                                        zIndex: 1
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setEndDateFilter(null);
                                    }}
                                >
                                    <ClearIcon fontSize="small" />
                                </IconButton>
                            )}
                        </div>
                    </Grid>
                </Grid>
            )
        }
        return null;
    }

    function handleBillingClick(id:number){
        const request = requests.find((req) => req.id === id);
        if(!request){
            console.log("No se encontró la solicitud");
            return;
        }
        // Optional chain: same defensive pattern as the other sites in this file.
        // Without the guard, a request with an empty `requestsServiceInvestigation`
        // array crashes the /pharmacy page with the same "Cannot read properties
        // of undefined" error as the displayed rows.
        const uuid = request.requestsServiceInvestigation?.[0]?.patientInvestigation?.uuid;
        if (!uuid) {
            console.log("Request has no patientInvestigation uuid; skipping billing navigation");
            return;
        }
        goToBillingPatientCallback(uuid);
    }

    async function handlePDFClick(id:number){
        const request = requests.find((req) => req.id === id);
        if(!request){
            console.log("No se encontró la solicitud");
            return;
        }
        
        setShowModalPDF(true);
        setLoadingPDFData(true);

        const patientUuid = request.requestsServiceInvestigation?.[0]?.patientInvestigation?.uuid;
        if (!patientUuid) {
            console.log("Request has no patientInvestigation uuid; cannot build PDF");
            setLoadingPDFData(false);
            return;
        }
        const patient = await fetchPatient(patientUuid);
        setPatientForPDF(patient);

        // Fetch patient and full submission data for PDF
        if(request.submissionPatient){
            // Fetch the complete submission with all surveyRecords
            const submissionData = await getSubmissionPatientService(uuidInvestigation, request.submissionPatient.id, false);
            console.log("submissionData", JSON.stringify(submissionData, null, 2));
            setFullSubmission(submissionData.submission);
        }
        
        setRequestPDF(request);
        setLoadingPDFData(false);
    }

    function handleAppointmentClick(id:number){
        const request = requests.find((req) => req.id === id);
        if(!request){
            console.log("No se encontró la solicitud");
        }
        else if(request.appointment){
            console.log("Ya existe una cita para esta solicitud");
            goToAppointmentFromRequestCallback(request.appointment.uuid);
        }
        else{
            createAppointmentFromRequestCallback(id)
        }
    }
    function fillRequest(id:number){
        const request = requests.find((req) => req.id === id);
        if(request){
            //Lo comento porque no entiendo por que solo permito entrar para algunos casos
            // const hasActionableRequests = ACTIONABLE_REQUESTS.filter(function(status) {
            //     if(serviceType === RequestType.PHARMACY){
            //         return status === request.status
            //     }
            //     return request.requestsServiceInvestigation.findIndex((req) => req.status === status) !== -1
            // }).length > 0;
            const hasActionableRequests = true;
            if(hasActionableRequests){
                callBackRequestSelected(request);
            }
            else{
                console.log("No hay survey");
            }
        }
        else{
            console.log("No hay nada que hacer con esa solicitud");
        }
        
    }
    if(loading || !departments){
        return <Loader />
    }
    else if(requests.length === 0){
        return(
            <div className="text-center">
                <h4><Translate id="pages.hospital.services.no_requests" /></h4>
            </div>
        )
    }
    // SHER-910: the column sets live in requestTableColumns.ts and are pinned by its tests.
    // Cordonnerie, Laboratory and Radiology now render the same list — one department column,
    // the origin — and Surgery is the only table that diverges. Editing a column here rather
    // than there would change every table that shares the default, silently.
    const headCells: HeadCell[] = buildRequestTableColumns(requestServiceType, !uuidPatient, stringHealthID)
        .map((column) => ({ id: column.id, label: <Translate id={column.labelId} />, alignment: 'left' as const }));
     
    let actions: any[] | null = null;
    if(requestServiceType === ServiceType.SURGERY){
        actions = [{type : (id:number) => iconType(id), color: (id:number) => colorOfAppointment(id), func : (id:number) => handleAppointmentClick(id), check: (row:Row) => row.statusValue !== RequestStatus.CANCELED}];
        
        // Only add billing action if billing functionality is enabled
        if(isBillingEnabled()) {
            actions.push({type : () => "billing" as const, color: () => "black", func : (id:number) => handleBillingClick(id), check: (row:Row) => row.statusValue !== RequestStatus.CANCELED});
        }
        
        // Add PDF action for surgery requests with submissions (SHER-1101: only when PDF export is enabled for the tenant)
        if(isPdfExportEnabled()) {
            actions.push({type : () => "pdf" as const, color: () => "red", func : (id:number) => handlePDFClick(id), check: (row:Row) => row.submissionPatient != null});
        }
    }
    else{
        // SHER-1101: only expose the PDF row action when PDF export is enabled for the tenant.
        actions = isPdfExportEnabled() ? [{type : () => "pdf", color: () => "red", func : (id:number) => handlePDFClick(id)}] : null
    }

    const renderExtraActions = uuidPatient && permissions && setShowSnackbar
        ? (row: Row) => {
            const request = requestsById.get(row.id);
            if (!request) return null;
            return (
                <CancelRequestAction
                    request={request}
                    uuidInvestigation={uuidInvestigation}
                    permissions={permissions}
                    variant="icon"
                    onCancelled={() => { refetchRequests?.(); }}
                    setShowSnackbar={setShowSnackbar}
                />
            );
        }
        : undefined;

    // SHER-462 — export/print controls belong to the global surgical waiting list
    // ("Liste chirurgicale"), not the per-patient request view.
    const isSurgeryWaitingList = requestServiceType === ServiceType.SURGERY && !uuidPatient;

    const surgicalExportLabels: SurgicalListLabels = {
        nhc: t(stringHealthID),
        patient: t('general.patient'),
        dob: t('investigation.create.personal_data.short-fields.birthdate'),
        mainSurgeon: t('pages.hospital.services.main_surgeon'),
        department: t('hospital.departments.department'),
        service: t('pages.hospital.services.surgery'),
        cim11: t('pages.hospital.services.request.surgery_indication'),
        dateAdded: t('general.date_added'),
        dateCompleted: t('hospital.request.dateCompleted'),
        totalDays: t('hospital.request.total_days'),
        status: t('pages.hospital.services.status'),
    };

    const toSurgicalExportRow = (row: Row): SurgicalListRow => ({
        nhc: String(row.nhc ?? ''),
        patient: row.patient ?? '',
        dob: row.dob ?? '',
        mainSurgeon: row.mainSurgeon ?? '',
        department: row.department ?? '',
        service: row.serviceName ?? '',
        cim11Full: row.cim11Text ?? '-', // full diagnosis, never the truncated cell
        dateAdded: row.dateAdded ?? '',
        dateCompleted: row.dateCompleted ?? '',
        totalDays: String(row.totalDays ?? ''),
        status: t(`pages.hospital.services.request_status.${getRequestStatusName(row.statusValue)}`),
    });

    const handleExportSurgicalCsv = () => {
        const csv = buildSurgicalListCsv(filteredRows.map(toSurgicalExportRow), surgicalExportLabels, !uuidPatient);
        downloadCsv(`surgical_list_${uuidInvestigation}.csv`, csv);
    };

    return (
        <Grid container spacing={2}>
            <Modal open={showModalPDF} closeModal={() => {
                setShowModalPDF(false);
                setFullSubmission(null);
                setPatientForPDF(null);
                setRequestPDF(null);
                setLoadingPDFData(false);
            }}>
                {loadingPDFData ? (
                    <Loader />
                ) : requestPDF && pdfConfig && pdfConfig.config ? (
                    <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
                        <h3><Translate id="pages.request.modal.download_title" />{requestPDF.id}</h3>
                        <p><Translate id="pages.request.modal.download_description" /></p>
                        
                        <div style={{ textAlign: 'center', marginTop: '20px' }}>
                            {isPdfExportEnabled() && (
                            <PDFDownloadButton
                                document={
                                    <RequestPDFDocument 
                                        request={{
                                            ...requestPDF,
                                            id: requestPDF.id.toString(),
                                            createdAt: requestPDF.createdAt instanceof Date ? requestPDF.createdAt.toISOString() : requestPDF.createdAt,
                                            completionDate: requestPDF.completedAt ? (requestPDF.completedAt instanceof Date ? requestPDF.completedAt.toISOString() : requestPDF.completedAt) : undefined
                                        } as any}
                                        patient={patientForPDF || {
                                            personalData: {
                                                name: 'Unknown',
                                                surnames: 'Patient',
                                                birthdate: '',
                                                sex: '',
                                                phone: '',
                                                healthId: '',
                                                nationalId: '',
                                                passportNumber: '',
                                            }
                                        }}
                                        submission={fullSubmission}
                                        surveyName={surveys?.find((survey) => survey.uuid === fullSubmission?.uuidSurvey)?.name}
                                        config={pdfConfig.config}
                                        locale={normalizedLocale}
                                        uuidInvestigation={currentInvestigationUuid || uuidInvestigation || ''}
                                    />
                                }
                                filename={`${t('pages.request.pdf.filename', 'medical-request')}-${requestPDF.id}`}
                                variant="contained"
                                size="large"
                                onSuccess={() => {
                                    console.log('PDF downloaded successfully');
                                }}
                                onError={(error) => {
                                    console.error('PDF download failed:', error);
                                }}
                                locale={normalizedLocale}
                            >
                                <Translate id="general.download_pdf" />
                            </PDFDownloadButton>
                            )}
                        </div>
                        
                        <div style={{ marginTop: '20px', fontSize: '12px', color: '#666' }}>
                            <p><strong><Translate id="pages.request.modal.pdf_content_title" /></strong></p>
                            <ul>
                                <li><Translate id="pages.request.modal.pdf_content_patient_info" /></li>
                                <li><Translate id="pages.request.modal.pdf_content_request_details" /></li>
                                <li><Translate id="pages.request.modal.pdf_content_services" /></li>
                                <li><Translate id="pages.request.modal.pdf_content_clinical_notes" /></li>
                                <li><Translate id="pages.request.modal.pdf_content_survey_records" /></li>
                                <li><Translate id="pages.request.modal.pdf_content_signature" /></li>
                            </ul>
                        </div>
                    </div>
                ) : (
                    <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto', textAlign: 'center' }}>
                        <h3>Configuration Error</h3>
                        <p style={{ color: 'red' }}>
                            PDF configuration is not available. Please check billing information.
                        </p>
                    </div>
                )}
            </Modal>
            {
                !noSearchBox &&
                // SHER-1061: request-specific copy; SearchBox's defaults belong to the billing screens.
                <SearchBox textField={{label:searchFor, helperText:"pages.hospital.services.search_box.helperText", callBack:setSearchText}}
                    activeFilters={statusFilter}
                    extraFilters={extraFilters}
                    filterItems={filterItems}
                    filterLabelId="pages.hospital.services.search_box.filter" />
            }
            {
                isSurgeryWaitingList &&
                <Grid item xs={12} style={{ textAlign: 'right' }} className="surgical-list-toolbar">
                    <Tooltip title={<Translate id="general.export_excel" />}>
                        <IconButton data-testid="surgical-export-excel" onClick={handleExportSurgicalCsv} color="primary">
                            <img src={ExcelLogo} alt="Excel" style={{ width: 24, height: 24 }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={<Translate id="general.print" />}>
                        <IconButton data-testid="surgical-print" onClick={() => window.print()} color="primary">
                            <PrintIcon />
                        </IconButton>
                    </Tooltip>
                </Grid>
            }
            {
                // SHER-462 — reuse the repo's print primitive (window.print + @media
                // print, as in DocumentPDF) but scope it to the table so only the
                // surgical list prints, not the surrounding app chrome/controls.
                isSurgeryWaitingList &&
                <style>{`@media print {
                    body * { visibility: hidden; }
                    #surgical-list-printable, #surgical-list-printable * { visibility: visible; }
                    #surgical-list-printable { position: absolute; left: 0; top: 0; width: 100%; }
                    .surgical-list-toolbar { display: none !important; }
                }`}</style>
            }
            <Grid item xs={12}>
                <div id={isSurgeryWaitingList ? 'surgical-list-printable' : undefined}>
                    <EnhancedTable  selectRow={(id:number) => fillRequest(id)} noHeader noSelectable
                        rows={filteredRows} headCells={headCells} actions={actions}
                        currentPage={tablePage} changePageCallback={setTablePage}
                        renderExtraActions={renderExtraActions}
                         />
                </div>
            </Grid>
        </Grid>
    );
};
